import { useState } from 'react';
import { Modal } from '../../components/common';
import { IconSave } from '../../components/icons';
import { Switch } from './shell';

/**
 * เตียง — built from tools/captures/dialog-bed.json.
 *
 * Four controls and no sections, and the same dialog creates and edits. ห้องนวด
 * is one of them because on their page a bed is moved between rooms from the
 * bed itself, not by dragging it.
 */
export default function BedDialog({ bed, roomId, rooms, onSubmit, onClose }) {
  const [v, setV] = useState(() => ({
    /* The room comes from the card the bed was clicked in — the row itself does
       not carry one, so reading bed.room_id only ever found undefined. */
    // Creating from the page-level เตียง choice preselects the first room in
    // ThaiHand; it does not start on an unassigned placeholder.
    roomId: roomId ?? rooms[0]?.id ?? '',
    name: bed?.name || '',
    code: bed?.code || '',
    // A newly created bed is inactive on ThaiHand until the user explicitly
    // turns it on. Rooms have the opposite default.
    isActive: bed ? bed.isActive !== false : false,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setV((s) => ({ ...s, [key]: value }));

  const submit = async () => {
    if (!v.name.trim()) {
      setError('กรุณากรอกชื่อเตียง');
      return;
    }
    if (!v.code.trim()) {
      setError('กรุณากรอกรหัสย่อเตียง');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      /* จำนวนที่ is left out because their dialog has none. Omitting it from the
         payload keeps whatever the bed already holds rather than resetting it. */
      await onSubmit({
        roomId: Number(v.roomId) || null,
        name: v.name.trim(),
        code: v.code.trim(),
        isActive: v.isActive,
      });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={bed ? 'แก้ไขเตียง' : 'สร้างเตียง'}
      className="bed-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={submit} disabled={busy}>
            <IconSave size={16} /> {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      )}
    >
      {error && <div className="login-error">{error}</div>}

      <div className="svc-section">
        <div className="field inline">
          <label>ห้องนวด</label>
          <select value={v.roomId} onChange={(e) => set('roomId', e.target.value)}>
            <option value="">ไม่ระบุห้อง</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>{r.name_th || r.code}</option>
            ))}
          </select>
        </div>

        <div className="grid-2">
          <div className="field">
            <label>ชื่อเตียง <span style={{ color: 'var(--red-600)' }}>*</span></label>
            <input
              value={v.name}
              placeholder="เตียง2"
              onChange={(e) => set('name', e.target.value)}
            />
          </div>
          {/* The short code repeats across rooms — ต2 is in more than one — so it
              is unique per room rather than per branch. */}
          <div className="field">
            <label>รหัสย่อเตียง <span style={{ color: 'var(--red-600)' }}>*</span></label>
            <input
              value={v.code}
              placeholder="ต2"
              onChange={(e) => set('code', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="svc-section">
        <Switch
          label="สถานะ"
          hint="ไม่ใช้งาน / ใช้งาน"
          on={v.isActive}
          onChange={(x) => set('isActive', x)}
        />
      </div>
    </Modal>
  );
}
